// pages/PedidosPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { getPedidos, getPedido, createPedido, updatePedido, deletePedido } from "../api/pedidos.js";
import { createDetallePedido, deleteDetallePedido, getDetallesByPedido } from "../api/detallePedido.js";
import { getClientes } from "../api/clientes.js";
import { getRestaurantes } from "../api/restaurantes.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { getProductos } from "../api/productos.js";
import { useAuth } from "../context/AuthContext";
import Pagination from "../components/Pagination";


const PedidosPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [pedidos, setPedidos] = useState([]);
    const [filteredPedidos, setFilteredPedidos] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [clientes, setClientes] = useState([]);
    const [restaurantes, setRestaurantes] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const inputRefs = useRef({});

    const [pedido, setPedido] = useState({ id_cliente: "", id_restaurante: "", fecha_pedido: "", estado: "pendiente", total: "0.00", direccion_entrega: "", metodo_entrega: "local" });
    const { sortedItems: sortedPedidos, sortConfig, setSortConfig } = useSortableData(filteredPedidos);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentPedidos, totalPages, itemsPerPage } = usePagination(sortedPedidos, 20);
    const [productos, setProductos] = useState([]);
    const [detalles, setDetalles] = useState([]);
    const [nuevoDetalle, setNuevoDetalle] = useState({ id_producto: "", cantidad: 1, descuento: 0 });

    // 🔹 Cargar clientes
    useEffect(() => {
        const loadClientes = async () => {
        try {
            const data = await getClientes();
            setClientes(Array.isArray(data) ? data : data.clientes || []);
        } catch (err) {
            console.error("Error al cargar clientes:", err);
        }
        };
        loadClientes();
    }, []);

    // 🔹 Cargar restaurantes
    useEffect(() => {
        const loadRestaurantes = async () => {
        try {
            const data = await getRestaurantes();
            setRestaurantes(Array.isArray(data) ? data : data.restaurantes || []);
        } catch (err) {
            console.error("Error al cargar restaurantes:", err);
        }
        };
        loadRestaurantes();
    }, []);

    // 🔹 Cargar pedidos
    const loadPedidos = async () => {
        try {
        const data = await getPedidos();
        const lista = Array.isArray(data) ? data : data.pedidos || [];
        setPedidos(lista);
        setFilteredPedidos(lista);
        } catch (err) {
        console.error("Error al cargar pedidos:", err);
        }
    };

    useEffect(() => {
        loadPedidos();
    }, []);

    // 🔹 Cargar productos
    useEffect(() => {
    const loadProductos = async () => {
        try {
        const data = await getProductos();
        setProductos(Array.isArray(data) ? data : data.productos || []);
        } catch (err) {
        console.error("Error al cargar productos:", err);
        }
    };
    loadProductos();
    }, []);

    // 🔍 Filtro de búsqueda
    useEffect(() => {
        const filtered = pedidos.filter((p) =>
        Object.values(p).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
        );
        setFilteredPedidos(filtered);
    }, [searchTerm, pedidos]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPedido({ ...pedido, [name]: value });
        
        // Limpiar error de dirección si cambia el método de entrega a "local"
        if (name === "metodo_entrega" && value === "local" && errors.direccion_entrega) {
            setErrors({ ...errors, direccion_entrega: null });
        }
    };

    const resetForm = () => {
        setPedido({ id_cliente: "", id_restaurante: "", fecha_pedido: "", estado: "pendiente", total: "0.00", direccion_entrega: "", metodo_entrega: "local" });
        setDetalles([]);
        setErrors({});
        setEditMode(false);
    };

    const validatePedido = (data) => {
        const newErrors = {};
        if (!data.id_cliente) newErrors.id_cliente = "Seleccione un cliente.";
        if (!data.id_restaurante) newErrors.id_restaurante = "Seleccione un restaurante.";
        
        // Validar fecha del pedido
        if (!data.fecha_pedido) {
            newErrors.fecha_pedido = "Seleccione una fecha para el pedido.";
        } else {
            const fechaPedido = new Date(data.fecha_pedido);
            if (isNaN(fechaPedido.getTime())) {
                newErrors.fecha_pedido = "La fecha del pedido no es válida.";
            }
        }
        
        if (!data.total || isNaN(data.total) || data.total <= 0)
            newErrors.total = "Ingrese un total válido.";
        if (!data.metodo_entrega) newErrors.metodo_entrega = "Seleccione un método de entrega.";
        
        // Validar dirección de entrega (obligatoria solo para delivery)
        if (data.metodo_entrega === "delivery") {
            if (!data.direccion_entrega || data.direccion_entrega.trim() === "") {
                newErrors.direccion_entrega = "La dirección de entrega es obligatoria para delivery.";
            }
        }
        // Validar que tenga al menos un detalle
        if (detalles.length === 0) {
            newErrors.detalles = "Agregue al menos un producto al pedido.";
        }
        
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validatePedido(pedido);
        if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        const firstErrorField = Object.keys(validationErrors)[0];
        const fieldRef = inputRefs.current[firstErrorField];
        if (fieldRef?.scrollIntoView) {
            fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            fieldRef.focus();
        }
        return;
        }

        try {
        // Solo enviar datos del pedido (sin detalles)
        const dataPedido = { ...pedido };
        
        if (editMode) {
            // Actualizar pedido
            await updatePedido(pedido.id_pedido, dataPedido);
            
            // Eliminar detalles existentes
            const detallesExistentes = await getDetallesByPedido(pedido.id_pedido);
            for (const detalle of detallesExistentes) {
                await deleteDetallePedido(detalle.id_detalle);
            }
            
            // Crear nuevos detalles (el backend calculará automáticamente los totales)
            for (const detalle of detalles) {
                await createDetallePedido({
                    id_pedido: pedido.id_pedido,
                    id_producto: parseInt(detalle.id_producto),
                    cantidad: parseInt(detalle.cantidad),
                    descuento: parseFloat(detalle.descuento) || 0
                });
            }
            
            // Actualizar totales desde el backend (ya calculados automáticamente)
            await actualizarTotalesDesdeBackend(pedido.id_pedido);
            alert("Pedido actualizado correctamente");
        } else {
            // Crear pedido
            const response = await createPedido(dataPedido);
            const nuevoPedido = response.pedido || response;
            const idPedido = nuevoPedido.id_pedido;
            
            // Crear detalles (el backend calculará automáticamente los totales)
            for (const detalle of detalles) {
                await createDetallePedido({
                    id_pedido: idPedido,
                    id_producto: parseInt(detalle.id_producto),
                    cantidad: parseInt(detalle.cantidad),
                    descuento: parseFloat(detalle.descuento) || 0
                });
            }
            
            // Actualizar totales desde el backend (ya calculados automáticamente)
            await actualizarTotalesDesdeBackend(idPedido);
            
            alert("Pedido creado correctamente");
        }
        resetForm();
        setModalOpen(false);
        loadPedidos();
        } catch (err) {
        console.error("Error del backend:", err.response?.data || err.message);
        const backendMsg =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "Error al guardar el pedido.";
        setErrors(prev => ({ ...prev, general: backendMsg }));
        alert(backendMsg);
        }
    };

    const handleEdit = async (p) => {
        setPedido(p);
        setEditMode(true);
        
        // Cargar detalles del pedido
        try {
            const detallesExistentes = await getDetallesByPedido(p.id_pedido);
            const detallesFormateados = detallesExistentes.map(detalle => {
                // Buscar el producto completo para tener toda la información
                const productoCompleto = productos.find(prod => prod.id_producto === detalle.id_producto);
                
                return {
                    id_producto: detalle.id_producto,
                    cantidad: detalle.cantidad,
                    descuento: detalle.descuento || 0,
                    precio_unitario: detalle.precio_unitario,
                    subtotal: detalle.subtotal,
                    producto: productoCompleto || {
                        id_producto: detalle.id_producto,
                        nombre: detalle.producto?.nombre || "Producto desconocido",
                        precio: detalle.precio_unitario,
                        iva_codigo: detalle.producto?.iva_codigo || '0'
                    }
                };
            });
            setDetalles(detallesFormateados);
            
            // Los totales ya vienen correctos del pedido seleccionado
            // No necesitamos recalcular, solo asegurar que estén en el estado
        } catch (error) {
            console.error("Error al cargar detalles:", error);
            setDetalles([]);
        }
        
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar este pedido? Esto también eliminará todos sus detalles.")) {
            try {
                // El backend ahora maneja la eliminación en cascada automáticamente
                await deletePedido(id);
                loadPedidos();
                alert("Pedido eliminado correctamente");
            } catch (error) {
                console.error("Error al eliminar pedido:", error);
                alert("Error al eliminar el pedido: " + (error.response?.data?.message || error.message));
            }
        }
    };

    // Función para actualizar totales del pedido basándose en los detalles del backend
    const actualizarTotalesDesdeBackend = async (idPedido) => {
        try {
            // Obtener el pedido actualizado del backend (que ya tiene los totales calculados)
            const response = await getPedido(idPedido);
            const pedidoActualizado = response.pedido || response; // Manejar ambas estructuras
            
            console.log("📥 Pedido actualizado del backend:", pedidoActualizado);
            
            setPedido(prev => ({
                ...prev,
                total: pedidoActualizado.total || "0.00"
            }));
            
            return pedidoActualizado;
        } catch (error) {
            console.error("Error al obtener totales del backend:", error);
            // Fallback: resetear a ceros si hay error
            setPedido(prev => ({
                ...prev,
                total: "0.00"
            }));
        }
    };

    //Logica para Detalle Pedidos
    const handleChangeDetalle = (e) => {
        const { name, value } = e.target;
        setNuevoDetalle({ ...nuevoDetalle, [name]: value });
    };

    const handleAddDetalle = () => {
        if (!nuevoDetalle.id_producto) {
            alert("Seleccione un producto");
            return;
        }
        if (!pedido.id_restaurante) {
            alert("Primero seleccione un restaurante");
            return;
        }
        
        const producto = productos.find(p => p.id_producto === parseInt(nuevoDetalle.id_producto));
        if (!producto) return;

        // Calcular subtotal temporal para mostrar en la interfaz
        const cantidad = parseInt(nuevoDetalle.cantidad);
        const precio = parseFloat(producto.precio);
        const descuento = parseFloat(nuevoDetalle.descuento) || 0;
        const subtotalCalculado = (precio * cantidad) - descuento;

        const nuevoDetalleCompleto = {
            id_producto: parseInt(nuevoDetalle.id_producto),
            cantidad: cantidad,
            descuento: descuento,
            producto,
            precio_unitario: precio,
            subtotal: subtotalCalculado
        };

        const detallesActualizados = [...detalles, nuevoDetalleCompleto];
        setDetalles(detallesActualizados);
        // Solo mostrar totales temporales en el frontend (los reales se calculan en el backend)
        const subtotalTemp = detallesActualizados.reduce((acc, d) => {
            const precio = parseFloat(d.producto?.precio) || 0;
            const cantidad = parseInt(d.cantidad) || 0;
            const descuento = parseFloat(d.descuento) || 0;
            const lineTotal = (precio * cantidad) - descuento;
            return acc + (isNaN(lineTotal) ? 0 : lineTotal);
        }, 0);

        setPedido(prev => ({
            ...prev,
            total: subtotalTemp.toFixed(2)
        }));
        
        setNuevoDetalle({ id_producto: "", cantidad: 1, descuento: 0 });
        
        // Limpiar errores relacionados con detalles y total
        if (errors.detalles || errors.total) {
            setErrors(prev => ({
                ...prev,
                detalles: null,
                total: null
            }));
        }
    };

    const handleRemoveDetalle = (index) => {
        const detallesActualizados = [...detalles];
        detallesActualizados.splice(index, 1);
        setDetalles(detallesActualizados);
        
        // Recalcular totales temporales
        if (detallesActualizados.length === 0) {
            setPedido(prev => ({
                ...prev,
                total: "0.00"
            }));
        } else {
            const subtotalTemp = detallesActualizados.reduce((acc, d) => {
                const precio = parseFloat(d.producto?.precio) || 0;
                const cantidad = parseInt(d.cantidad) || 0;
                const descuento = parseFloat(d.descuento) || 0;
                const lineTotal = (precio * cantidad) - descuento;
                return acc + (isNaN(lineTotal) ? 0 : lineTotal);
            }, 0);
            
            setPedido(prev => ({
                ...prev,
                total: subtotalTemp.toFixed(2)
            }));
        }
        
        // Si no quedan detalles, limpiar error de total
        if (detallesActualizados.length === 0 && errors.total) {
            setErrors(prev => ({
                ...prev,
                total: null
            }));
        }
    };

    // Recalcular totales cuando cambie el restaurante
    const handleRestauranteChange = (e) => {
        const { name, value } = e.target;
        setPedido({ ...pedido, [name]: value });
        
        // Si hay detalles y se cambió el restaurante, recalcular totales temporales
        if (name === 'id_restaurante' && detalles.length > 0) {
            const subtotalTemp = detalles.reduce((acc, d) => {
                const precio = parseFloat(d.producto?.precio) || 0;
                const cantidad = parseInt(d.cantidad) || 0;
                const descuento = parseFloat(d.descuento) || 0;
                const lineTotal = (precio * cantidad) - descuento;
                return acc + (isNaN(lineTotal) ? 0 : lineTotal);
            }, 0);
            
            setPedido(prev => ({
                ...prev,
                total: subtotalTemp.toFixed(2)
            }));
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Pedidos</h1>

        {/* 🔍 Buscador + Botón */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
            <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nuevo Pedido </button>
        </div>

        {/* 🧾 Tabla de Pedidos */}
        <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
            <table className="min-w-full text-sm text-left divide-y divide-gray-200">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
                <tr>
                <SortableHeader label="N°" sortKey="id_pedido" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <th className="px-4 py-2">Cliente</th>
                {usuarioLogueado?.isSuperAdmin && (
                    <SortableHeader label="Restaurante" sortKey="restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                )}
                <th className="px-4 py-2">Fecha</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Total</th>
                <th className="px-4 py-2">Entrega</th>
                <th className="px-4 py-2 text-center">Acciones</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
                {currentPedidos.length > 0 ? (
                currentPedidos.map((p, idx) => (
                    <tr key={p.id_pedido} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td className="px-4 py-2">{p.cliente?.nombre || "—"}</td>
                    {usuarioLogueado?.isSuperAdmin && (
                        <td className="px-4 py-2">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                {p.restaurante?.nombre_comercial || "Sin restaurante"}
                            </span>
                        </td>
                    )}
                    <td className="px-4 py-2">{p.fecha_pedido?.split("T")[0]}</td>
                    <td className="px-4 py-2 capitalize">{p.estado}</td>
                    <td className="px-4 py-2">${p.total}</td>
                    <td className="px-4 py-2">{p.metodo_entrega}</td>
                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(p)} > Editar </button>
                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(p.id_pedido)} > Eliminar </button>
                    </td>
                    </tr>
                ))
                ) : (
                <tr>
                    <td colSpan={usuarioLogueado?.isSuperAdmin ? "8" : "7"} className="text-center py-4 text-gray-500"> No hay pedidos registrados </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>

        {/* Paginación */}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

        {/* 🔧 Modal para Crear/Editar Pedido */}
        {modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                    <h2 className="text-2xl font-bold mb-4"> {editMode ? "Editar Pedido" : "Crear Nuevo Pedido"} </h2>

                    <form onSubmit={handleSubmit}>
                        {/* Información básica del pedido */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <InputGroup label="Cliente" error={errors.id_cliente}>
                                <select
                                    name="id_cliente"
                                    value={pedido.id_cliente}
                                    onChange={handleChange}
                                    ref={(el) => (inputRefs.current.id_cliente = el)}
                                    className="input-base"
                                >
                                    <option value="">Seleccione un cliente</option>
                                    {clientes.map((c) => (
                                        <option key={c.id_cliente} value={c.id_cliente}>
                                            {c.nombre}
                                        </option>
                                    ))}
                                </select>
                            </InputGroup>

                            <InputGroup label="Restaurante" error={errors.id_restaurante}>
                                <select
                                    name="id_restaurante"
                                    value={pedido.id_restaurante}
                                    onChange={handleRestauranteChange}
                                    ref={(el) => (inputRefs.current.id_restaurante = el)}
                                    className="input-base"
                                >
                                    <option value="">Seleccione un restaurante</option>
                                    {restaurantes.map((r) => (
                                        <option key={r.id_restaurante} value={r.id_restaurante}>
                                            {r.nombre_comercial} (IVA: {r.iva_vigente}%)
                                        </option>
                                    ))}
                                </select>
                            </InputGroup>
                            <InputGroup label="Fecha del Pedido" error={errors.fecha_pedido}> <input type="date" name="fecha_pedido" value={pedido.fecha_pedido} onChange={handleChange} ref={(el) => (inputRefs.current.fecha_pedido = el)} className="input-base" required /> </InputGroup>
                            <InputGroup label="Estado"> <select name="estado" value={pedido.estado} onChange={handleChange} className="input-base" > <option value="pendiente">Pendiente</option> <option value="en preparación">En Preparación</option> <option value="entregado">Entregado</option> <option value="cancelado">Cancelado</option> </select> </InputGroup>
                            <InputGroup label="Método de Entrega" error={errors.metodo_entrega}> <select name="metodo_entrega" value={pedido.metodo_entrega} onChange={handleChange} ref={(el) => (inputRefs.current.metodo_entrega = el)} className="input-base" > <option value="local">Local</option> <option value="delivery">Delivery</option> </select> </InputGroup>
                            <InputGroup label="Dirección de Entrega" error={errors.direccion_entrega}> <input type="text" name="direccion_entrega" value={pedido.direccion_entrega} onChange={handleChange} ref={(el) => (inputRefs.current.direccion_entrega = el)} className="input-base" placeholder={pedido.metodo_entrega === "delivery" ? "Dirección (obligatoria)" : "Dirección (opcional)"} /> </InputGroup>
                        </div>

                        {/* Detalle del Pedido */}
                        <div className="border-t border-gray-300 pt-6 mt-6">
                            <h3 className="text-xl font-semibold mb-4 text-gray-800">Detalle del Pedido</h3>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                                <InputGroup label="Producto">
                                    <select
                                        name="id_producto"
                                        value={nuevoDetalle.id_producto}
                                        onChange={handleChangeDetalle}
                                        className="input-base"
                                    >
                                        <option value="">Seleccione un producto</option>
                                        {productos.map((p) => (
                                            <option key={p.id_producto} value={p.id_producto}>
                                                {p.nombre} - ${p.precio}
                                            </option>
                                        ))}
                                    </select>
                                </InputGroup>
                                <InputGroup label="Cantidad"> <input type="number" name="cantidad" min="1" value={nuevoDetalle.cantidad} onChange={handleChangeDetalle} className="input-base" placeholder="Cantidad" /> </InputGroup>
                                <InputGroup label="Descuento"> <input type="number" name="descuento" step="0.01" value={nuevoDetalle.descuento} onChange={handleChangeDetalle} className="input-base" placeholder="Descuento" /> </InputGroup>
                                <div className="flex items-end">
                                    <button type="button" onClick={handleAddDetalle} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded w-full" > ➕ Agregar </button>
                                </div>
                            </div>

                            {detalles.length > 0 && (
                                <div className="overflow-x-auto mb-4">
                                    <table className="w-full border text-sm text-gray-700">
                                        <thead className="bg-gray-100 text-gray-800">
                                            <tr>
                                                <th className="p-2 text-left">Producto</th>
                                                <th className="p-2 text-center">Cant.</th>
                                                <th className="p-2 text-center">P. Unit.</th>
                                                <th className="p-2 text-center">Desc.</th>
                                                <th className="p-2 text-center">Subtotal</th>
                                                <th className="p-2 text-center">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detalles.map((d, i) => (
                                                <tr key={i} className="border-t">
                                                    <td className="p-2">{d.producto.nombre}</td>
                                                    <td className="p-2 text-center">{d.cantidad}</td>
                                                    <td className="p-2 text-center">${d.producto.precio}</td>
                                                    <td className="p-2 text-center">${d.descuento || 0}</td>
                                                    <td className="p-2 text-center font-semibold">${typeof d.subtotal === 'number' ? d.subtotal.toFixed(2) : parseFloat(d.subtotal || 0).toFixed(2)}</td>
                                                    <td className="p-2 text-center">
                                                        <button type="button" onClick={() => handleRemoveDetalle(i)} className="text-red-500 hover:text-red-700" > ✖ </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {errors.detalles && (
                                <p className="text-red-600 text-sm mb-4">{errors.detalles}</p>
                            )}

                            {/* Resumen de totales */}
                            <div className="col-span-full bg-gray-50 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold mb-3 text-gray-800">Total del Pedido</h4>
                                <div className="grid grid-cols-1 gap-4">
                                    <InputGroup label="Total" error={errors.total}> <input type="number" name="total" step="0.01" value={pedido.total || "0.00"} readOnly ref={(el) => (inputRefs.current.total = el)} className="input-base bg-gray-100 font-bold text-lg" placeholder="0.00" /> </InputGroup>
                                </div>
                            </div>
                        </div>

                        {/* Mostrar errores generales y de detalles */}
                        {(errors.general || errors.detalles) && (
                            <div className="text-red-600 text-sm text-center mb-4">
                                {errors.general && <p>{errors.general}</p>}
                                {errors.detalles && <p>{errors.detalles}</p>}
                            </div>
                        )}

                        <div className="flex justify-end gap-4 mt-6">
                            <button type="button" onClick={() => { setModalOpen(false); resetForm(); }} className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600" > Cancelar </button>
                            <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700" > {editMode ? "Actualizar" : "Guardar"} </button>
                        </div>
                    </form>
                </div>
            </div>
        )}
        </div>
    );
};

export default PedidosPage;
